import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { faqApi } from '../services/api';
import AnimatedSection from './AnimatedSection';
import StaggeredList from './StaggeredList';

const FAQSection = () => {
  const [faq, setFaq] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFAQ();
  }, []);

  const fetchFAQ = async () => {
    try {
      const response = await faqApi.getFAQ();
      if (!Array.isArray(response.data)) throw new Error('no backend');
      setFaq(response.data);
    } catch (error) {
      console.error('Error fetching FAQ:', error);
      // Fallback to mock data
      setFaq([
        {
          id: 1,
          question: "How long does it take to install the Cordflex Clip?",
          answer: "Installation takes just 30 seconds. The intuitive design uses a simple spring-loaded clip to organize lines, making it easy for staff to use immediately.",
        },
        {
          id: 2,
          question: "Is training required for staff?",
          answer: "Minimal training is required - just 5 minutes of staff training is sufficient for full implementation."
        },
        {
          id: 3,
          question: "What types of medical lines does it support?",
          answer: "Cordflex accommodates most line, tube, cable, and cord types and attaches to almost any surface (e.g. bed rail, bed headboard, IV Pole, walker, wheelchair, patient gown, etc.). It has 2-degree freedom of movement attachments that allow lines to slide, and can even be re-arranged."
        },
        {
          id: 4,
          question: "Is the Cordflex Clip sterile?",
          answer: "Cordflex is a clean, single-patient-use, non-sterile device. It is a Class I, 510(k)-exempt medical device listed with FDA, intended for use by healthcare professionals and trained caregivers in hospitals, long-term care, assisted living and home care settings."
        },
        {
          id: 5,
          question: "What kind of results can we expect?",
          answer: "Published clinical studies on other line management devices (not Cordflex) have reported faster line identification, higher nurse satisfaction, reductions in falls, line pullouts, medical errors, and infection risks, and increased motivation for patient ambulation. Cordflex itself has not yet been studied for these outcomes. Check out the Resource Library for more information."
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading FAQ...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <AnimatedSection animation="fade-up">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#214140' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get answers to common questions about Cordflex implementation and usage
            </p>
          </div>
        </AnimatedSection>

        <div className="max-w-3xl mx-auto">
          <AnimatedSection animation="fade-up" delay={200}>
            <Accordion type="single" collapsible className="space-y-4">
              <StaggeredList delay={100}>
                {faq.map((item, index) => (
                  <AccordionItem 
                    key={item.id || index} 
                    value={`item-${index}`}
                    className="border border-gray-200 rounded-lg px-6 hover:shadow-md transition-shadow duration-200"
                  >
                    <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline py-6" style={{ color: '#214140' }}>
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700 leading-relaxed pb-6">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </StaggeredList>
            </Accordion>
          </AnimatedSection>

          <div className="text-center mt-10">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5"
              style={{ backgroundColor: '#214140' }}
            >
              View All FAQs
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;